import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { openInNewTab } from "@/utils/open-in-new-tab";
import {
  logGuideVideoOpen,
  logInvectorAiOpen,
  logUserGuideOpen,
} from "@/utils/usage-log";
import { ExternalLink, X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { FAQS, type Faq } from "./ai-faq";
import type { Step } from "./steps";

const AI_CONSULT_URL = "/help";

const BotIcon = () => (
  <img className="size-6 shrink-0" src="/icons/invector-ai-bot.svg" alt="" />
);

const BotBubble = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full flex-col items-start gap-1">
    <BotIcon />
    <div className="flex w-full flex-col gap-4 rounded-[20px] bg-background-gray-alternative px-4 py-2.5 label-1-normal-medium whitespace-pre-line text-label-neutral">
      {children}
    </div>
  </div>
);

const Chip = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    className={cn(
      "rounded-full border border-line-normal bg-white px-4 py-2.5 text-left label-1-normal-medium text-label-normal hover:border-label-neutral hover:bg-background-gray-alternative",
      className,
    )}
    {...props}
  />
);

const MoreHelpChip = () => (
  // 그라데이션 테두리: 안쪽 배경은 padding-box 에, 그라데이션은 border-box 에 깐다.
  // 안쪽 배경이 그라데이션 레이어라 Chip 의 hover:bg 가 가려진다 — 호버 색은 변수로 바꾼다.
  // 파란 띠가 좌우를 오가는 모션은 AI 물어보기 버튼과 같다(ai-ask-flip) — 분홍 바탕에 파란 띠를
  // 둔 패턴을 폭 200% 로 깔고 왕복시킨다. 멈춰 있을 때(0% 위치)가 Figma 의 분홍→파랑 그대로다
  <Chip
    className="animate-ai-ask-flip-fast border-[1.5px] border-transparent [--more-help-bg:white] [background:linear-gradient(var(--more-help-bg),var(--more-help-bg))_0_0/100%_100%_padding-box,linear-gradient(90deg,#fb64b6_0%,#51a2ff_50%,#fb64b6_100%)_0_0/200%_100%_border-box] hover:border-transparent hover:[--more-help-bg:var(--background-gray-alternative)] motion-reduce:animate-none"
    onClick={() => {
      logInvectorAiOpen("AI 물어보기: 더 궁금한 점");
      openInNewTab(AI_CONSULT_URL);
    }}
  >
    🤖 더 궁금한 점 물어보기
  </Chip>
);

const Answer = ({
  faq,
  canOpenStep,
  onOpenStep,
}: {
  faq: Faq;
  canOpenStep: (stepId: Step["id"]) => boolean;
  onOpenStep: (stepId: Step["id"]) => void;
}) => {
  const { guide, cta } = faq;
  const blocked = !canOpenStep(cta.stepId);

  return (
    <BotBubble>
      <p>{faq.answer}</p>
      {guide && (
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-lg bg-gray-200 px-4 py-3 text-left label-2-medium text-label-neutral hover:bg-line-normal"
          onClick={() => {
            const entry = `AI 물어보기: ${faq.question}`;
            if (guide.kind === "video") logGuideVideoOpen(entry);
            else logUserGuideOpen(entry);
            openInNewTab(guide.url);
          }}
        >
          <span className="flex-1">{guide.label}</span>
          <ExternalLink
            className="size-[18px] shrink-0 text-blue-500"
            strokeWidth={1.8}
          />
        </button>
      )}
      <div className="flex w-full flex-col gap-1">
        <Button
          variant="outlined"
          color="primary"
          size={40}
          className="w-full bg-white"
          disabled={blocked}
          onClick={() => onOpenStep(cta.stepId)}
        >
          {cta.label}
        </Button>
        {/* 눌러도 아무 일이 없는 대신, 왜 막혔는지 보여 준다 */}
        {blocked && (
          <p className="label-2-regular text-label-alternative">
            파일 변환이 끝나면 이동할 수 있습니다.
          </p>
        )}
      </div>
    </BotBubble>
  );
};

export const AiChatWindow = ({
  faq,
  answering,
  canOpenStep,
  onSelect,
  onOpenStep,
  onClose,
}: {
  faq: Faq | null;
  /** 정해진 답변이지만 대화처럼 보이도록 잠시 로딩을 보여 주는 중 */
  answering: boolean;
  /** 변환 중 등으로 그 STEP 을 열 수 없으면 CTA 를 막는다 */
  canOpenStep: (stepId: Step["id"]) => boolean;
  onSelect: (faq: Faq | null) => void;
  onOpenStep: (stepId: Step["id"]) => void;
  onClose: () => void;
}) => {
  const { user } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);

  // 로딩·답변이 나타날 때마다 대화 맨 아래를 보여 준다
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [faq, answering]);

  useEffect(() => {
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <>
      {/* 바깥 클릭은 이 막이 받는다. 아래 화면(main 의 패널 닫기, STEP 카드)까지 클릭이 가지 않는다 */}
      <div
        data-testid="ai-chat-backdrop"
        className="fixed inset-0 z-20"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="인벡터 AI"
        className="fixed right-10 bottom-10 z-20 flex animate-chat-open h-[600px] max-h-[calc(100vh-80px)] w-[400px] flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-xlarge"
      >
        <header className="flex shrink-0 items-center gap-1.5 border-b border-line-normal p-4">
          <BotIcon />
          <div className="flex flex-1 items-center gap-1.5">
            <span className="body-1-normal-medium text-label-normal">
              인벡터 AI
            </span>
            <button
              type="button"
              aria-label="AI 상담 새 탭으로 열기"
              onClick={() => {
                logInvectorAiOpen("AI 물어보기: AI 상담으로 이동");
                openInNewTab(AI_CONSULT_URL);
              }}
            >
              <ExternalLink
                className="size-[18px] text-label-neutral"
                strokeWidth={1.8}
              />
            </button>
          </div>
          {/* autoFocus: 열릴 때 포커스를 창 안으로 들인다. 바깥 막이 마우스를 막는 것과 짝 */}
          <button type="button" autoFocus aria-label="닫기" onClick={onClose}>
            <X className="size-6 text-label-normal" strokeWidth={1.8} />
          </button>
        </header>
        <div
          ref={scrollRef}
          className="flex flex-1 scroll-smooth flex-col gap-2 overflow-y-auto px-4 pt-3 pb-4 [&>*]:shrink-0"
        >
          <BotBubble>
            안녕하세요{user?.displayName ? `, ${user.displayName}님` : ""}. 어떤
            점이 궁금하세요?
          </BotBubble>
          {faq && (
            <>
              <p className="max-w-full self-end break-words rounded-[20px] bg-label-neutral px-4 py-2.5 mt-2 label-1-normal-semibold text-white">
                {faq.question}
              </p>
              {answering ? (
                <BotBubble>
                  <div
                    role="status"
                    aria-label="답변 작성 중"
                    className="flex h-6 items-center gap-1 px-1"
                  >
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        className="size-1.5 animate-chat-wave rounded-full bg-[#51a2ff]"
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </div>
                </BotBubble>
              ) : (
                <Answer
                  faq={faq}
                  canOpenStep={canOpenStep}
                  onOpenStep={onOpenStep}
                />
              )}
            </>
          )}
          {!answering && (
            <div className="flex flex-col items-end gap-2">
              {faq ? (
                <Chip onClick={() => onSelect(null)}>🔄 다시 선택하기</Chip>
              ) : (
                FAQS.map((f) => (
                  <Chip key={f.question} onClick={() => onSelect(f)}>
                    {f.question}
                  </Chip>
                ))
              )}
              <MoreHelpChip />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
